if (!customElements.get('home-lovers-form')) {
  class HomeLoversForm extends HTMLElement {
    // The submit button is not always a descendant of the form: the membership
    // overlay renders it in the panel's pinned footer and binds it back with the
    // HTML `form` attribute. form.elements covers both — it lists every listed
    // element whose form owner is this form, however it was associated — so the
    // standalone sign-up page (button inside the form) resolves identically.
    static submitButton(form) {
      return form ? Array.from(form.elements).find((el) => el.hasAttribute('data-submit-btn')) || null : null;
    }

    // Joins the dial code and the typed number into the one phone string sent to
    // the backend. Swedish numbers are written with a trunk 0 and hyphens
    // ("070-123 45 67"), which in international form drops the 0, so hyphens
    // become spaces and one leading 0 goes. Italy/Vatican City (+39), San Marino
    // (+378) and Côte d'Ivoire (+225) keep it — there it is part of the number.
    static phoneValue(dialCode, number) {
      const keepsLeadingZero = ['+39', '+378', '+225'];
      let national = (number || '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
      if (!keepsLeadingZero.includes(dialCode)) national = national.replace(/^0\s*/, '');
      return national ? [dialCode, national].filter(Boolean).join(' ') : '';
    }

    connectedCallback() {
      this._initFormSubmit();
      this._initPhoneCountryCode();
      this._initPhoneErrorClear();
      this._initMembershipCheck();
    }

    _initFormSubmit() {
      const form = this.closest('form');
      if (!form) return;

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._submitForm(form);
      });
    }

    // Clear a "phone already registered" error as soon as the customer edits
    // the number again, rather than leaving a stale error on a changed value.
    _initPhoneErrorClear() {
      this.querySelector('[data-phone-input]')?.addEventListener('input', () => {
        this._clearFieldError('phone');
      });
    }

    // The phone country code is a <custom-select> (assets/custom-select.js) whose
    // option value is the ISO country code (unique, unlike dial codes which many
    // countries share). It dispatches a bubbling 'change' CustomEvent with
    // detail.selectedValue rather than being a native form control — on change,
    // look up the dial code and mirror the flag/prefix/hidden input ourselves,
    // since custom-select.js only knows how to copy option text into the button.
    _initPhoneCountryCode() {
      const mapEl = this.querySelector('[data-phone-country-map]');
      let dialByIso = {};
      if (mapEl) {
        try {
          dialByIso = JSON.parse(mapEl.textContent);
        } catch (_) {}
      }

      this.addEventListener('change', (e) => {
        const wrapper = e.target.closest('[data-phone-country]');
        if (!wrapper) return;

        const iso = e.detail.selectedValue;
        const dial = dialByIso[iso] || '';

        const hiddenInput = wrapper.querySelector('[data-phone-country-code]');
        if (hiddenInput) hiddenInput.value = dial;

        const flagImg = wrapper.querySelector('[data-phone-flag-img]');
        if (flagImg) flagImg.src = `https://cdn.shopify.com/static/images/flags/${iso.toLowerCase()}.svg`;

        const prefixEl = wrapper.closest('.home-lovers-form__phone-row')?.querySelector('[data-phone-prefix]');
        if (prefixEl) prefixEl.textContent = dial;
      });
    }

    // Real-time check, on blur of either field, for whether this person is
    // already a Home Lovers member — if so there's no need for them to sign
    // up again. A stale response arriving after a newer check was already
    // issued (e.g. the customer fixes a typo and blurs again quickly) is
    // discarded via the requestId guard so it can't clobber the newer result.
    _initMembershipCheck() {
      const form = this.closest('form');
      const endpoint = form?.dataset.membershipEndpoint;
      const emailInput = this.querySelector('[data-email-input]');
      const phoneInput = this.querySelector('[data-phone-input]');
      if (!form || !endpoint || (!emailInput && !phoneInput)) return;

      let requestId = 0;

      const check = async () => {
        const email = emailInput?.value.trim() || '';
        const phoneCountryCode = form.querySelector('[data-phone-country-code]')?.value || '';
        const phone = HomeLoversForm.phoneValue(phoneCountryCode, phoneInput?.value);

        // Nothing to check (both cleared) — that's a non-member state, not a
        // no-op: lift a previously-shown notice rather than leaving it stuck.
        if (!email && !phone) {
          this._setMembershipStatus(false, false);
          return;
        }

        const body = {};
        if (email) body.email = email;
        if (phone) body.phone = phone;

        const thisRequest = ++requestId;
        let data;
        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!res.ok) return;
          data = await res.json();
        } catch (_) {
          return;
        }

        if (thisRequest !== requestId) return;
        this._setMembershipStatus(data?.emailIsMember, data?.phoneIsTaken);
      };

      emailInput?.addEventListener('blur', check);
      phoneInput?.addEventListener('blur', check);

      // A logged-in customer gets email/phone prefilled (see snippets/home-lovers-form.liquid) —
      // they may tick consent and submit without ever blurring either field, so the blur-only
      // checks above would never run. Run one immediately if either already has a value.
      if (emailInput?.value.trim() || phoneInput?.value.trim()) check();
    }

    // Email/phone stay editable so the customer can fix a typo — re-blurring
    // afterwards re-checks and updates which notice(s) show. isMember,
    // phoneTaken, and submitting are tracked separately (rather than the
    // submit button's own disabled state) so a slow membership check
    // resolving after the customer has already clicked submit can't
    // re-enable it mid-request, and a failed/errored submit can't re-enable
    // it while still flagged. emailIsMember means the email already belongs
    // to a Home Lovers member; phoneIsTaken means the phone already belongs
    // to some existing Shopify customer (member or not) — Shopify enforces
    // phone uniqueness across customers, so that phone would fail
    // registration regardless of membership status.
    _setMembershipStatus(emailIsMember, phoneIsTaken) {
      this._isMember = !!emailIsMember;
      this._phoneTaken = !!phoneIsTaken;
      this._updateSubmitAvailability();
      this.querySelector('[data-email-membership-notice]')?.toggleAttribute('hidden', !emailIsMember);
      this.querySelector('[data-phone-membership-notice]')?.toggleAttribute('hidden', !phoneIsTaken);
    }

    _updateSubmitAvailability() {
      const submitBtn = HomeLoversForm.submitButton(this.closest('form'));
      if (submitBtn) submitBtn.disabled = !!this._submitting || !!this._isMember || !!this._phoneTaken;
    }

    async _submitForm(form) {
      const endpoint = form.dataset.endpoint;
      const variant = form.dataset.variant || 'short';
      const submitBtn = HomeLoversForm.submitButton(form);
      const errorEl = form.querySelector('[data-submit-error]');
      const formData = new FormData(form);
      const homeSize = formData.get('home_size');
      const phoneCountryCode = formData.get('phone_country_code') || '';
      const phoneNumber = formData.get('phone') || '';

      const payload = {
        formId: 'ilva-home-lovers-signup',
        variant,
        firstName: formData.get('first_name') || '',
        lastName: formData.get('last_name') || '',
        email: formData.get('email') || '',
        phone: HomeLoversForm.phoneValue(phoneCountryCode, phoneNumber),
        birthdate: formData.get('birthday') || '',
        houseTypes: formData.getAll('house_type'),
        houseSize: homeSize ? parseInt(homeSize, 10) : null,
        favoriteStoreId: formData.get('favorite_store') || '',
        consent: formData.has('consent'),
        marketing: formData.has('marketing'),
      };

      this._clearFieldError('phone');
      errorEl?.setAttribute('hidden', '');

      // Phone is required for membership. The input's `required` + `pattern`
      // stop an empty field natively; this catches what still normalises to
      // nothing (e.g. a lone trunk "0" stripped by phoneValue), or a form
      // rendered by an older cached snippet without the attributes.
      if (!payload.phone) {
        const phoneInput = this.querySelector('[data-phone-input]');
        this._setFieldError('phone', phoneInput?.dataset.requiredMessage || '');
        if (!phoneInput) this._showSubmitError(form, null);
        return;
      }

      this._submitting = true;
      this._updateSubmitAvailability();
      if (submitBtn) submitBtn.textContent = submitBtn.dataset.labelLoading || submitBtn.textContent;

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          // Backend convention (matches the extension PATCH handler): 422 with
          // { error, field? } — field is present for field-specific failures
          // like a duplicate phone number, absent for everything else.
          const data = await res.json().catch(() => null);
          this._showSubmitError(form, data);
          return;
        }

        this._submitting = false;
        this._showSuccess(form);
      } catch (_) {
        this._showSubmitError(form, null);
      }
    }

    _showSubmitError(form, data) {
      this._submitting = false;
      const submitBtn = HomeLoversForm.submitButton(form);
      if (submitBtn) submitBtn.textContent = submitBtn.dataset.labelDefault || submitBtn.textContent;
      this._updateSubmitAvailability();

      if (data?.field) {
        this._setFieldError(data.field, data.error);
        return;
      }

      const errorEl = form.querySelector('[data-submit-error]');
      const errorText = errorEl?.querySelector('[data-submit-error-text]');
      if (errorText) errorText.textContent = data?.error || errorEl.dataset.defaultMessage || errorText.textContent;
      errorEl?.removeAttribute('hidden');
      // The error renders inside the form, which in the membership overlay is a
      // scrolling body — with the submit button pinned in the panel footer the
      // customer can be scrolled past the error and see nothing happen. Bring it
      // to them; scrollIntoView finds the right scroll container on its own.
      errorEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    _setFieldError(field, message) {
      const input = this.querySelector(`[data-${field}-input]`);
      const errorEl = this.querySelector(`[data-${field}-field-error]`);
      if (!input || !errorEl) return;

      input.setAttribute('aria-invalid', 'true');
      input.closest('.home-lovers-form__phone-row')?.classList.add('is-invalid');
      errorEl.textContent = message || '';
      errorEl.removeAttribute('hidden');
      input.focus();
    }

    _clearFieldError(field) {
      const input = this.querySelector(`[data-${field}-input]`);
      const errorEl = this.querySelector(`[data-${field}-field-error]`);
      if (!input || !errorEl) return;

      input.removeAttribute('aria-invalid');
      input.closest('.home-lovers-form__phone-row')?.classList.remove('is-invalid');
      errorEl.setAttribute('hidden', '');
    }

    _showSuccess(form) {
      const page = this.closest('.home-lovers-form-page');
      const successEl = page?.querySelector('[data-success]');
      successEl?.removeAttribute('hidden');
      form.setAttribute('hidden', '');

      // Hiding the form no longer hides a submit button that lives outside it, so
      // retire the external one explicitly. Hide its wrapper rather than the button:
      // the wrapper is a flex item in the overlay footer, and hiding the button alone
      // would leave its row and gap behind.
      const externalSubmit = HomeLoversForm.submitButton(form);
      if (externalSubmit && !form.contains(externalSubmit)) {
        externalSubmit.closest('[data-membership-submit]')?.setAttribute('hidden', '');
      }

      // Bring the confirmation to the top of whatever scrolls it: the document on the
      // sign-up page, .membership-overlay__body in the cart overlay. scrollIntoView walks
      // every scrollable ancestor, so one call covers both — window.scrollTo would be a
      // no-op in the overlay (the panel is overflow:hidden and the body carries
      // .overflow-hidden while it is open) and would land on the Image banner on the page.
      // Deferred one frame because hiding the form collapses a tall element: scrolling
      // before that reflow targets a stale layout and the browser clamps the offset.
      if (successEl) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        requestAnimationFrame(() => {
          successEl.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
          // The confirmation carries no role="status", and the customer's focus is left on
          // a now-hidden submit button, so move focus onto the message to announce it.
          if (!successEl.hasAttribute('tabindex')) successEl.setAttribute('tabindex', '-1');
          successEl.focus({ preventScroll: true });
        });
      }

      // Fallback hook the membership overlay listens for (assets/membership-signup.js)
      // to tick the cart checkbox and re-poke the cart for the member price — the
      // overlay's own detection only knows how to watch a Shopify Forms app block.
      // `context` tells datalayer.js which GA4 event this is: the Bliv medlem page
      // form reports create_account, the cart overlay's create_account_in_cart.
      // Decided by the ancestor, not by .home-lovers-form-page (the overlay's embed
      // carries that class too) or the URL (/pages/bliv-medlem-1 renders both forms).
      const context = this.closest('membership-overlay') ? 'overlay' : 'page';
      document.dispatchEvent(new CustomEvent('membership:signed-up', { detail: { context } }));
    }
  }

  customElements.define('home-lovers-form', HomeLoversForm);
}
